export type DebtKind = "auto" | "cards" | "student" | "other";
export interface DebtItem { id: string; kind: DebtKind; name: string; balance: number; apr: number; min: number; aprEstimated?: boolean; minEstimated?: boolean; dueDay?: number; dueDate?: string }

// Next monthly occurrence of a debt's due date (or legacy due-day number), rolled forward when it has passed.
export function nextDue(d: { dueDate?: string; dueDay?: number }): string | null {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const start = d.dueDate ? new Date(`${d.dueDate}T00:00:00`) : null;
  const day = start && !isNaN(start.getTime()) ? start.getDate() : d.dueDay ?? null;
  if (!day || !Number.isFinite(day)) return null;
  let y = start && !isNaN(start.getTime()) ? start.getFullYear() : today.getFullYear();
  let m = start && !isNaN(start.getTime()) ? start.getMonth() : today.getMonth();
  for (let i = 0; i < 48; i++) {
    const dd = Math.min(day, new Date(y, m + 1, 0).getDate());
    const date = new Date(y, m, dd);
    if (date >= today) return `${y}-${String(m + 1).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
    m++; if (m > 11) { m = 0; y++; }
  }
  return null;
}
export type Method = "avalanche" | "snowball" | "hybrid" | "payment" | "snowflake" | "consolidate" | "minimums";

export const METHODS: { key: Method; title: string; sub: string; bestFor: string }[] = [
  { key: "snowball", title: "Snowball", sub: "Smallest balance first (Dave Ramsey)", bestFor: "Quick wins keep you motivated" },
  { key: "avalanche", title: "Avalanche", sub: "Highest APR first", bestFor: "You trust the math, cheapest on paper" },
  { key: "hybrid", title: "Hybrid", sub: "High-APR debts, smallest first", bestFor: "Wins early without wasting interest" },
  { key: "payment", title: "Highest payment", sub: "Free up the biggest bill first", bestFor: "Monthly cash flow is tight" },
  { key: "snowflake", title: "Snowflake", sub: "Small found money on top (avalanche order)", bestFor: "Budget is strict, but you can save small daily amounts" },
  { key: "consolidate", title: "Balance transfer / loan", sub: "Move debt to a lower rate", bestFor: "You'd open a new card or loan and won't add new debt" },
  { key: "minimums", title: "Minimums only", sub: "If nothing changes", bestFor: "Shows the cost of waiting" },
];

export interface SimOpts { snowflake?: number; consApr?: number; consIntroMonths?: number; consFeePct?: number; consRevertApr?: number }
export const DEFAULT_OPTS: Required<SimOpts> = { snowflake: 150, consApr: 0, consIntroMonths: 18, consFeePct: 3, consRevertApr: 22 };

export interface PayoffResult { months: number; interest: number; order: string[]; curve: number[]; stalled: boolean }

type D = DebtItem;
function sorterFor(method: Method, all: D[]): (a: D, b: D) => number {
  if (method === "avalanche") return (a, b) => b.apr - a.apr;
  if (method === "snowball") return (a, b) => a.balance - b.balance;
  if (method === "payment") return (a, b) => b.min - a.min;
  if (method === "hybrid") {
    const avg = all.reduce((s, d) => s + d.apr, 0) / Math.max(1, all.length);
    return (a, b) => {
      const ha = a.apr >= avg ? 0 : 1, hb = b.apr >= avg ? 0 : 1;
      return ha !== hb ? ha - hb : a.balance - b.balance;
    };
  }
  return () => 0;
}

/** Month-by-month payoff: pay all minimums, roll extra + freed minimums onto the target debt. */
export type LiabilityKind = "child_support" | "alimony" | "court" | "tax" | "medical" | "other";
export interface Liability { id: string; kind: LiabilityKind; name: string; monthly: number; owed?: number; endDate?: string }
export const LIABILITY_LABEL: Record<LiabilityKind, string> = { child_support: "Child support", alimony: "Alimony", court: "Court fees / fines", tax: "Tax payment plan", medical: "Medical payment plan", other: "Other" };

/** Months until a liability ends: owed ÷ monthly, or months to its end date. null = ongoing / unknown. */
export function liabilityMonths(l: Liability): number | null {
  if (l.owed && l.owed > 0 && l.monthly > 0) return Math.ceil(l.owed / l.monthly);
  if (l.endDate) {
    const e = new Date(l.endDate), n = new Date();
    return Math.max(0, (e.getFullYear() - n.getFullYear()) * 12 + e.getMonth() - n.getMonth());
  }
  return null;
}

export function simulate(debts: DebtItem[], extra: number, method: Method, liabilities: Liability[] = [], opts: SimOpts = {}): PayoffResult {
  const o = { ...DEFAULT_OPTS, ...opts };
  let ds = debts.filter((d) => d.balance > 0).map((d, i) => ({ ...d, name: d.name || `Debt ${i + 1}` }));
  if (method === "consolidate" && ds.length) {
    ds = [{ ...ds[0], id: "cons", name: "Consolidated", balance: ds.reduce((a, d) => a + d.balance, 0) * (1 + o.consFeePct / 100), apr: o.consApr, min: ds.reduce((a, d) => a + d.min, 0) }];
  }
  if (method === "snowflake") extra = extra + Math.max(0, o.snowflake);
  const minimumsOnly = method === "minimums";
  const budget = ds.reduce((a, d) => a + d.min, 0) + (minimumsOnly ? 0 : Math.max(0, extra));
  const sorter = sorterFor(method === "snowflake" || method === "consolidate" ? "avalanche" : method, ds);
  const order: string[] = [];
  const curve = [ds.reduce((a, d) => a + d.balance, 0)];
  let interest = 0, m = 0;
  const ends = liabilities.filter((l) => l.monthly > 0).map((l) => ({ monthly: l.monthly, months: liabilityMonths(l) }));
  while (ds.some((d) => d.balance > 0.01) && m < 600) {
    m++;
    const freed = minimumsOnly ? 0 : ends.reduce((a, l) => a + (l.months != null && l.months < m ? l.monthly : 0), 0);
    for (const d of ds) if (d.balance > 0) { const apr = method === "consolidate" && m > o.consIntroMonths ? o.consRevertApr : d.apr; const i = d.balance * (apr / 100 / 12); d.balance += i; interest += i; }
    let left = minimumsOnly ? Infinity : budget + freed;
    for (const d of ds) if (d.balance > 0) { const p = Math.min(d.min, d.balance, left); d.balance -= p; if (!minimumsOnly) left -= p; }
    if (!minimumsOnly) for (const d of [...ds].filter((x) => x.balance > 0).sort(sorter)) { if (left <= 0) break; const p = Math.min(d.balance, left); d.balance -= p; left -= p; }
    for (const d of ds) if (d.balance <= 0.01 && !order.includes(d.name)) { d.balance = 0; order.push(d.name); }
    curve.push(ds.reduce((a, d) => a + d.balance, 0));
  }
  return { months: m, interest, order, curve, stalled: m >= 600 };
}

export const nextTarget = (debts: DebtItem[], method: Method) => {
  const live = debts.filter((d) => d.balance > 0);
  return [...live].sort(sorterFor(["minimums", "snowflake", "consolidate"].includes(method) ? "avalanche" : method, live))[0]?.name ?? null;
};

const TERM: Record<DebtKind, number> = { cards: 0, auto: 60, student: 120, other: 36 };
/** Typical minimum: cards = 1% + interest (≥$25); loans = amortized over a typical term. */
export function estimateMin(kind: DebtKind, balance: number, apr: number) {
  if (!balance) return 0;
  const r = apr / 100 / 12;
  if (kind === "cards") return Math.min(balance, Math.max(25, Math.round(balance * 0.01 + balance * r)));
  const n = TERM[kind];
  return Math.round(r ? (balance * r) / (1 - Math.pow(1 + r, -n)) : balance / n);
}

export const CARD_PROVIDERS = [
  "Chase Sapphire Preferred", "Chase Freedom Unlimited", "Capital One Quicksilver", "Capital One Venture", "Discover it Cash Back",
  "Amex Blue Cash Everyday", "Amex Gold", "Citi Double Cash", "Bank of America Customized Cash", "Wells Fargo Active Cash",
  "Synchrony store card", "Other",
];
